import React, { FC, useMemo } from 'react';
import { defineMessages, FormattedNumber, useIntl } from 'react-intl';
import { PremiumPrice } from 'src/api/internal/schema';
import { getPricePerMonth } from 'src/utils/payment/paymentPlansUtils';
import * as S from './styles';

const msgs = defineMessages({
  discount: {
    id: 'ea_plans_percent_off',
  },
  popular: {
    id: 'onboarding_upsell_all_pro_plans_badge_popular_plan',
  },
});

const msgsPerPeriod = {
  month: defineMessages({
    cardTitle: { id: 'ea_plans_monthly' },
    periodName: { id: 'month' },
    discountOnce: { id: 'ea_plans_percent_off_1_month_web' },
  }),
  year: defineMessages({
    cardTitle: { id: 'ea_plans_annual' },
    periodName: { id: 'year' },
    discountOnce: { id: 'ea_plans_percent_off_1_year_web' },
  }),
  lifetime: defineMessages({
    cardTitle: { id: 'ea_plans_lifetime' },
    periodName: { id: 'payment_page_total_label' },
    discountOnce: { id: 'ea_plans_percent_off' },
  }),
};

export const PriceCard: FC<{
  price: PremiumPrice;
  selected: boolean;
  showAsPopular?: boolean;
  onClick: () => void;
}> = ({ selected, price, showAsPopular, onClick }) => {
  const intl = useIntl();

  const { paymentPeriod, currency, discount, finalPrice, fullPrice } = price;
  const periodMsgs = msgsPerPeriod[paymentPeriod];

  const periodText = useMemo(() => {
    if (paymentPeriod === 'lifetime') {
      return intl.formatMessage(periodMsgs.periodName).toLocaleLowerCase();
    }

    if (discount && discount.duration === 'once') {
      return null;
    }

    return '/' + intl.formatMessage(periodMsgs.periodName);
  }, [intl, paymentPeriod, discount, periodMsgs]);

  const badgeText = useMemo(() => {
    if (!discount) {
      return showAsPopular ? intl.formatMessage(msgs.popular) : null;
    }

    return intl.formatMessage(
      discount.duration === 'forever' ? msgs.discount : periodMsgs.discountOnce,
      {
        percent: discount.percent,
      },
    );
  }, [intl, showAsPopular, discount, periodMsgs]);

  return (
    <S.PricesCard $selected={selected} onClick={onClick} data-testid="price-card">
      <S.Period>{intl.formatMessage(periodMsgs.cardTitle)}</S.Period>
      <S.RowWrapper>
        <S.RowWrapper>
          <S.PriceWrapper>
            <S.Price>
              <FormattedNumber value={finalPrice} style="currency" currency={currency} />
            </S.Price>
            {periodText && <S.PerPeriod>{periodText}</S.PerPeriod>}
          </S.PriceWrapper>
          {!!discount && (
            <S.PriceBeforeSale>
              <FormattedNumber value={fullPrice} style="currency" currency={currency} />
            </S.PriceBeforeSale>
          )}
        </S.RowWrapper>
        {paymentPeriod === 'year' && (
          <S.PricePerMonth>
            <FormattedNumber
              value={getPricePerMonth(finalPrice)}
              style="currency"
              currency={currency}
            />
            /{intl.formatMessage(msgsPerPeriod.month.periodName)}
          </S.PricePerMonth>
        )}
      </S.RowWrapper>
      {badgeText && <S.Badge>{badgeText}</S.Badge>}
    </S.PricesCard>
  );
};
